/**
 * Die Filterleiste der Seite "Aktivitaeten" (Phase 1, Task 8) - Neubau ohne Bestandsvorbild.
 *
 * KONTROLLIERT, OHNE EIGENEN FILTERZUSTAND: `ansicht` kommt von aussen (die Adresse, siehe
 * `state/adresse.ts#AktivitaetenAnsicht`), jede Bedienung ruft `onAendern` mit der KOMPLETTEN neuen
 * Ansicht. Die Leiste laedt nichts und ruft keine API - Optionen, Aktualisieren und CSV bringt die Seite.
 * Einzig der Suchtext hat einen Entwurf (`entwurf`): getippt wird schneller, als die Adresse folgen soll.
 *
 * ZEITRAUM: Kurzwahl (Heute, 7 Tage, 30 Tage, Alle) und ein Von/Bis-Paar (`DatumsBereichFilter`, ohne
 * "Nur ohne Wert"). Eine Kurzwahl loescht von/bis; ein Datum setzt `zr` auf die Vorgabe zurueck, denn ein
 * gesetztes von/bis gilt vorrangig (`AktivitaetenSeite.tsx#zeitraumAusAnsicht`). Der Berliner Tag, an dem
 * die Kurzwahl rechnet, steht dort - diese Datei kennt nur den Schluessel. Liegt Von nach Bis, steht an den
 * Datumsfeldern "Von liegt nach Bis" (`lib/aktivitaetenZeitraum.ts`), CSV und Aktualisieren sind gesperrt; die
 * Seite stellt dann keine Anfrage. Ein Jahr unter 1000 ist ein Tipp-Zwischenstand: dieselbe Sperre, kein Hinweis.
 *
 * AUSWAHLFENSTER ("Wer", "Bereich", "Wo", "Quelle"): `<details>` nach dem Muster von `RatenTabelle.tsx` -
 * gesteuert (`open`), nur eines gleichzeitig offen; Klick ausserhalb, Escape und der Schliessen-Knopf
 * schliessen (die beiden Letzteren geben den Fokus an die Beschriftung zurueck). Ohne geladene Optionen
 * zeigen Wer und Bereich "Optionen werden geladen …" bzw. "Optionen konnten nicht geladen werden." statt
 * einer scheinbar leeren Liste (`optionenFehler`). Die Widgets
 * (`MehrfachauswahlFilter`) sind die des Board-Spaltenfilters; ihr Aussehen haengt an
 * `.order-list-filter-menu` (`spaltenfilter.css`), deshalb traegt das Menue diese Klasse. NICHT
 * `.order-list-filter` selbst: das ist ein Kopfzellen-Symbol mit fester Hoehe (`bestand.css`).
 *
 * BEREICH UND FELD sind zwei Listen mit ODER-Wirkung im Server (Entscheidung 4): ein angehakter Bereich
 * steht in `bereich`, einzelne Felder ohne ihren Bereich in `feld`. Wer einen ganzen Bereich anhakt, nimmt
 * dessen Einzelfelder aus `feld` heraus (sie waeren doppelt gemeint); ein gewaehlter Bereich zeigt seine
 * Felder angehakt und gesperrt.
 *
 * WO (Phase 2, Task 11B): die Objektgruppen des Servers (`filter-optionen.wo`: Bestellungen, Sendungen, FX, Lieferanten,
 * Vorlagen, Verwaltung) in der Reihenfolge, die er liefert - ohne das Recht fuer die Verwaltung fehlt ihr Eintrag, die Leiste
 * fuegt nichts hinzu. Ein Wert je Gruppe in `wo` (wiederholbar), unabhaengig von Bereich und Feld.
 *
 * SUCHE: Uebernahme nach 300 ms Ruhe oder mit Enter, immer getrimmt; ein leerer Text ergibt kein `q`.
 * Der Timer liest die Ansicht ueber eine Ref beim FEUERN, nicht die des Renders, in dem er gesetzt wurde -
 * sonst wuerfe er eine Aenderung weg, die dazwischen kam. Dass `ansicht.q` von aussen kommt (Zuruecksetzen,
 * Zurueck-Taste), erkennt `uebernommen`: nur ein Wert, den diese Leiste NICHT selbst abgeschickt hat, ueber-
 * schreibt den Entwurf. Sonst loeschte das Echo der eigenen Uebernahme, was inzwischen weiter getippt wurde.
 */
import { useEffect, useRef, useState, type JSX, type KeyboardEvent, type ReactNode } from "react";
import {
  QUELLEN_LABEL,
  type AktivitaetenFilterOptionen,
  type BereichKey,
  type QuellenFilter,
  type WoFilter,
} from "../../api/aktivitaeten.ts";
import { zeitraumPruefen } from "../../lib/aktivitaetenZeitraum.ts";
import { frischeVorgabe, type AktivitaetenAnsicht } from "../../state/adresse.ts";
import { DatumsBereichFilter } from "../Board/filter/DatumsBereichFilter.tsx";
import { Ikone } from "../Ikone.tsx";
import { MehrfachauswahlFilter } from "../Board/filter/MehrfachauswahlFilter.tsx";

export interface AktivitaetenFilterleisteProps {
  ansicht: AktivitaetenAnsicht;
  /**
   * `null` = noch nicht geladen (oder nicht erreichbar): Wer und Bereich zeigen dann eine Meldung statt der
   * Liste, Quelle hat ihre Standardliste.
   */
  optionen: AktivitaetenFilterOptionen | null;
  /**
   * Der Text zum fehlgeschlagenen Abruf der Optionen (fester Satz + Ursache aus `fehlertext()`, von der Seite
   * gebaut). Wirkt nur bei `optionen === null` (diese Meldung statt "werden geladen"); schon geladene Listen
   * bleiben auch nach einem spaeteren Fehlschlag stehen. `null`/fehlend = kein Fehler.
   */
  optionenFehler?: string | null;
  onAendern: (a: AktivitaetenAnsicht) => void;
  onAktualisieren: () => void;
  onExport: () => void;
  exportLaeuft: boolean;
}

/** Ruhezeit der Suche, bevor der Text uebernommen wird. */
const SUCH_RUHE_MS = 300;

const KURZWAHL: readonly { zr: AktivitaetenAnsicht["zr"]; label: string }[] = [
  { zr: "heute", label: "Heute" },
  { zr: "7t", label: "7 Tage" },
  { zr: "30t", label: "30 Tage" },
  { zr: "alle", label: "Alle" },
];

/** Die Quellen, solange der Server keine Liste geliefert hat (`quelle` im Filter, siehe `QuellenFilter`). */
const STANDARD_QUELLEN: readonly QuellenFilter[] = ["tool", "jtl", "dsv", "system", "migration"];

type FensterId = "wer" | "bereich" | "wo" | "quelle";

/** Weicht die Ansicht von der Vorgabe ab? `dir` zaehlt nicht: die Sortierung ist kein Filter. */
function weichtVonVorgabeAb(a: AktivitaetenAnsicht): boolean {
  const vorgabe = frischeVorgabe().aktivitaeten;
  return (
    a.zr !== vorgabe.zr ||
    Boolean(a.von) ||
    Boolean(a.bis) ||
    a.wer.length > 0 ||
    a.bereich.length > 0 ||
    a.feld.length > 0 ||
    a.wo.length > 0 ||
    a.quelle.length > 0 ||
    a.q.trim() !== ""
  );
}

interface AuswahlfensterProps {
  id: FensterId;
  name: string;
  /** Anzahl der gewaehlten Eintraege; 0 = keine Zahl, kein Hervorheben. */
  zahl: number;
  offen: boolean;
  onToggle: (istOffen: boolean) => void;
  /** Der Schliessen-Knopf im Menue (schliesst und holt den Fokus auf die Beschriftung zurueck). */
  onSchliessen: () => void;
  children: ReactNode;
}

/**
 * Ein Auswahlfenster nach dem Muster `RatenTabelle.tsx`/`Spaltenkopf.tsx`: Beschriftung mit einem Namen, der
 * Kontext und Zahl vorliest ("Filter Wer, 2 ausgewählt" statt "Wer 2"), im Menue Schliessen-Knopf und
 * Ueberschrift ("Wer filtern"). Der Knopf `.spaltenfilter-schliessen` steht absolut oben rechts
 * (`spaltenfilter.css`, braucht `.order-list-filter-menu` als Vorfahren), die Ueberschrift laesst ihm Platz.
 */
function Auswahlfenster({ id, name, zahl, offen, onToggle, onSchliessen, children }: AuswahlfensterProps): JSX.Element {
  return (
    <details
      className={`akt-popover${zahl > 0 ? " active" : ""}`}
      data-fenster={id}
      open={offen}
      onToggle={(e) => onToggle((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary aria-label={zahl > 0 ? `Filter ${name}, ${zahl} ausgewählt` : `Filter ${name}`}>
        <span className="akt-popover-name">{name}</span>
        {zahl > 0 ? <span className="akt-popover-zahl">{zahl}</span> : null}
      </summary>
      <div className="order-list-filter-menu akt-popover-menue" hidden={!offen}>
        <button type="button" className="spaltenfilter-schliessen" aria-label={`${name}-Filter schließen`} onClick={onSchliessen}>
          <Ikone name="x" />
        </button>
        <div className="akt-popover-titel">{`${name} filtern`}</div>
        {children}
      </div>
    </details>
  );
}

export function AktivitaetenFilterleiste({
  ansicht,
  optionen,
  optionenFehler = null,
  onAendern,
  onAktualisieren,
  onExport,
  exportLaeuft,
}: AktivitaetenFilterleisteProps): JSX.Element {
  const leiste = useRef<HTMLDivElement>(null);
  const [offen, setOffen] = useState<FensterId | null>(null);
  /** Je Bereich der Aufklappzustand; fehlt der Eintrag, ist er aufgeklappt, sobald ein Feld darin gewaehlt ist. */
  const [aufgeklappt, setAufgeklappt] = useState<Record<string, boolean>>({});

  const emit = (teil: Partial<AktivitaetenAnsicht>): void => onAendern({ ...ansicht, ...teil });

  // ---- Auswahlfenster: ein Klick ausserhalb schliesst das offene (Capture, wie `RatenTabelle.tsx`:
  // Zeilen koennen die Ausbreitung stoppen). Ein Klick im offenen Fenster selbst bleibt ohne Wirkung.
  useEffect(() => {
    if (!offen) return;
    const aussen = (ereignis: MouseEvent): void => {
      const fenster = leiste.current?.querySelector(`details[data-fenster="${offen}"]`);
      if (fenster?.contains(ereignis.target as Node)) return;
      setOffen(null);
    };
    document.addEventListener("click", aussen, true);
    return () => document.removeEventListener("click", aussen, true);
  }, [offen]);

  const umschalten = (id: FensterId) => (istOffen: boolean) =>
    setOffen((alt) => (istOffen ? id : alt === id ? null : alt));

  /**
   * Schliesst das Fenster und gibt den Fokus an dessen Beschriftung zurueck - nur auf Verlangen
   * (`fokus`: Schliessen-Knopf, Escape im Fenster). Sonst bliebe der Fokus auf einem Element, das mit dem
   * Fenster verschwindet, und der naechste Tab begaenne am Seitenanfang. Ein Klick ausserhalb holt ihn nicht.
   */
  const schliessen = (id: FensterId, fokus: boolean): void => {
    setOffen(null);
    if (!fokus) return;
    leiste.current?.querySelector<HTMLElement>(`details[data-fenster="${id}"] > summary`)?.focus();
  };

  const escape = (ereignis: KeyboardEvent<HTMLDivElement>): void => {
    if (ereignis.key !== "Escape" || !offen) return;
    const fenster = leiste.current?.querySelector(`details[data-fenster="${offen}"]`);
    schliessen(offen, Boolean(fenster?.contains(ereignis.target as Node)));
  };

  // ---- Suche
  const [entwurf, setEntwurf] = useState(ansicht.q);
  const ansichtRef = useRef(ansicht);
  ansichtRef.current = ansicht;
  const aendernRef = useRef(onAendern);
  aendernRef.current = onAendern;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Der Suchtext, den diese Leiste zuletzt selbst abgeschickt (oder von aussen uebernommen) hat. */
  const uebernommen = useRef(ansicht.q);

  const timerLoeschen = (): void => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const uebernehmen = (text: string): void => {
    timerLoeschen();
    const q = text.trim();
    if (q === ansichtRef.current.q.trim()) return;
    uebernommen.current = q;
    aendernRef.current({ ...ansichtRef.current, q });
  };

  const sucheAendern = (text: string): void => {
    setEntwurf(text);
    timerLoeschen();
    timer.current = setTimeout(() => uebernehmen(text), SUCH_RUHE_MS);
  };

  const sucheTaste = (ereignis: KeyboardEvent<HTMLInputElement>): void => {
    if (ereignis.key !== "Enter") return;
    ereignis.preventDefault();
    uebernehmen(entwurf);
  };

  // Eine Aenderung von aussen (Zuruecksetzen, Zurueck-Taste, geoeffneter Link) ersetzt den Entwurf -
  // das Echo der eigenen Uebernahme nicht (siehe Kopfkommentar).
  useEffect(() => {
    if (ansicht.q === uebernommen.current) return;
    uebernommen.current = ansicht.q;
    timerLoeschen();
    setEntwurf(ansicht.q);
  }, [ansicht.q]);

  // Eine ausstehende Uebernahme ueberlebt den Abbau der Leiste nicht.
  useEffect(() => timerLoeschen, []);

  // ---- Zeitraum
  const kurzwahlGedrueckt = (zr: AktivitaetenAnsicht["zr"]): boolean =>
    ansicht.zr === zr && !ansicht.von && !ansicht.bis;

  const kurzwahl = (zr: AktivitaetenAnsicht["zr"]): void => {
    if (kurzwahlGedrueckt(zr)) return;
    emit({ zr, von: null, bis: null });
  };

  // Von nach Bis (oder ein Jahr, das noch getippt wird): die Seite laedt dann nicht (`AktivitaetenSeite.tsx`),
  // also sind auch CSV und Aktualisieren gesperrt. Den Hinweis gibt es nur beim echten Vertauschen, nicht beim
  // Zwischenstand - sonst blitzte er bei jeder Jahresziffer auf.
  const zeitraumStand = zeitraumPruefen(ansicht.von, ansicht.bis);
  const zeitraumGesperrt = zeitraumStand !== "ok";

  // Ohne Optionen: Meldung statt Liste, damit "laedt" und "nicht erreichbar" nicht wie "keine Eintraege" aussehen.
  const optionenMeldung =
    optionen !== null
      ? null
      : optionenFehler
        ? { text: optionenFehler, rolle: "alert" as const }
        : { text: "Optionen werden geladen …", rolle: "status" as const };
  const meldung = optionenMeldung ? (
    <p className="spaltenfilter-keine-treffer" role={optionenMeldung.rolle}>
      {optionenMeldung.text}
    </p>
  ) : null;

  // ---- Wer
  const werOptionen = [
    ...(optionen?.personen ?? []).map((p) => ({ value: p.key, label: p.label })),
    ...(optionen?.automatisch ?? []).map((a) => ({ value: a.key, label: `${a.label} (automatisch)` })),
  ];

  // ---- Bereich
  const bereiche = optionen?.bereiche ?? [];
  const bereichUmschalten = (key: BereichKey, felder: readonly { key: string }[]): void => {
    if (ansicht.bereich.includes(key)) {
      emit({ bereich: ansicht.bereich.filter((k) => k !== key) });
      return;
    }
    const eigene = new Set(felder.map((f) => f.key));
    emit({ bereich: [...ansicht.bereich, key], feld: ansicht.feld.filter((f) => !eigene.has(f)) });
  };
  const feldUmschalten = (key: string): void =>
    emit({ feld: ansicht.feld.includes(key) ? ansicht.feld.filter((f) => f !== key) : [...ansicht.feld, key] });

  // ---- Wo
  const woOptionen = (optionen?.wo ?? []).map((w) => ({ value: w.key, label: w.label }));
  // Gezaehlt wird, was als Option sichtbar ist: ein Tieflink auf einen Wert, den der Server diesem Nutzer nicht anbietet
  // (`?wo=verwaltung` ohne Recht), liefert eine leere Liste, soll aber nicht "1" zaehlen, ohne dass ein Haken zu sehen ist.
  // Ohne geladene Optionen laesst sich nichts ausschliessen - dann zaehlen alle Werte der Adresse.
  const woZahl =
    optionen === null ? ansicht.wo.length : ansicht.wo.filter((w) => woOptionen.some((o) => o.value === w)).length;

  // ---- Quelle
  const quellenOptionen = optionen
    ? optionen.quellen.map((q) => ({ value: q.key, label: q.label }))
    : STANDARD_QUELLEN.map((q) => ({ value: q, label: QUELLEN_LABEL[q] }));

  const zuruecksetzen = (): void => {
    timerLoeschen();
    uebernommen.current = "";
    setEntwurf("");
    onAendern({ ...frischeVorgabe().aktivitaeten, dir: ansicht.dir });
  };

  return (
    <div
      ref={leiste}
      className="akt-filter"
      role="group"
      aria-label="Aktivitäten filtern"
      onKeyDown={escape}
    >
      <div className="akt-zeitraum">
        <div className="akt-segment" role="group" aria-label="Zeitraum">
          {KURZWAHL.map(({ zr, label }) => (
            <button key={zr} type="button" aria-pressed={kurzwahlGedrueckt(zr)} onClick={() => kurzwahl(zr)}>
              {label}
            </button>
          ))}
        </div>
        <DatumsBereichFilter
          label="Zeitraum"
          bereich={{ von: ansicht.von ?? undefined, bis: ansicht.bis ?? undefined }}
          onAendern={(b) => emit({ zr: frischeVorgabe().aktivitaeten.zr, von: b.von || null, bis: b.bis || null })}
          ohneWertVerstecken
        />
        {zeitraumStand === "vertauscht" ? (
          <span className="akt-zeitraum-hinweis" role="status">
            Von liegt nach Bis
          </span>
        ) : null}
      </div>

      <Auswahlfenster
        id="wer"
        name="Wer"
        zahl={ansicht.wer.length}
        offen={offen === "wer"}
        onToggle={umschalten("wer")}
        onSchliessen={() => schliessen("wer", true)}
      >
        {meldung ?? (
          <MehrfachauswahlFilter
            label="Wer"
            optionen={werOptionen}
            ausgewaehlt={ansicht.wer}
            onAendern={(wer) => emit({ wer })}
          />
        )}
      </Auswahlfenster>

      <Auswahlfenster
        id="bereich"
        name="Bereich"
        zahl={ansicht.bereich.length + ansicht.feld.length}
        offen={offen === "bereich"}
        onToggle={umschalten("bereich")}
        onSchliessen={() => schliessen("bereich", true)}
      >
        {meldung ? (
          meldung
        ) : bereiche.length === 0 ? (
          <p className="spaltenfilter-keine-treffer">Keine Treffer.</p>
        ) : (
          <div className="akt-bereiche">
            {bereiche.map((b) => {
              const ganz = ansicht.bereich.includes(b.key);
              const klappOffen = aufgeklappt[b.key] ?? b.felder.some((f) => ansicht.feld.includes(f.key));
              return (
                <div key={b.key} className="akt-bereich">
                  <div className="akt-bereich-kopf">
                    <label className="spaltenfilter-option">
                      <input
                        type="checkbox"
                        aria-label={`Bereich: ${b.label}`}
                        checked={ganz}
                        onChange={() => bereichUmschalten(b.key, b.felder)}
                      />
                      <span>{b.label}</span>
                    </label>
                    {b.felder.length > 0 ? (
                      <button
                        type="button"
                        className="akt-aufklappen"
                        aria-expanded={klappOffen}
                        aria-label={`${b.label}: Felder ${klappOffen ? "einklappen" : "aufklappen"}`}
                        onClick={() => setAufgeklappt((alt) => ({ ...alt, [b.key]: !klappOffen }))}
                      >
                        {klappOffen ? "▾" : "▸"}
                      </button>
                    ) : null}
                  </div>
                  {klappOffen ? (
                    <div className="akt-felder" role="group" aria-label={`${b.label}-Felder`}>
                      {b.felder.map((f) => (
                        <label key={f.key} className="spaltenfilter-option">
                          <input
                            type="checkbox"
                            aria-label={`${b.label}: ${f.label}`}
                            checked={ganz || ansicht.feld.includes(f.key)}
                            disabled={ganz}
                            onChange={() => feldUmschalten(f.key)}
                          />
                          <span>{f.label}</span>
                        </label>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </Auswahlfenster>

      <Auswahlfenster
        id="wo"
        name="Wo"
        zahl={woZahl}
        offen={offen === "wo"}
        onToggle={umschalten("wo")}
        onSchliessen={() => schliessen("wo", true)}
      >
        {meldung ?? (
          <MehrfachauswahlFilter
            label="Wo"
            optionen={woOptionen}
            ausgewaehlt={ansicht.wo}
            onAendern={(wo) => emit({ wo: wo as WoFilter[] })}
          />
        )}
      </Auswahlfenster>

      <Auswahlfenster
        id="quelle"
        name="Quelle"
        zahl={ansicht.quelle.length}
        offen={offen === "quelle"}
        onToggle={umschalten("quelle")}
        onSchliessen={() => schliessen("quelle", true)}
      >
        <MehrfachauswahlFilter
          label="Quelle"
          optionen={quellenOptionen}
          ausgewaehlt={ansicht.quelle}
          onAendern={(quelle) => emit({ quelle: quelle as QuellenFilter[] })}
        />
      </Auswahlfenster>

      <input
        type="search"
        className="akt-suche"
        aria-label="Suche"
        placeholder="Bestellnummer, Lieferant, Artikel oder Wert"
        value={entwurf}
        onChange={(e) => sucheAendern(e.target.value)}
        onKeyDown={sucheTaste}
      />

      {weichtVonVorgabeAb(ansicht) ? (
        <button type="button" className="akt-zuruecksetzen" onClick={zuruecksetzen}>
          Filter zurücksetzen
        </button>
      ) : null}

      <div className="akt-filter-aktionen">
        <button type="button" className="btn-ghost" disabled={zeitraumGesperrt} onClick={onAktualisieren}>
          Aktualisieren
        </button>
        <button type="button" className="btn-ghost" disabled={exportLaeuft || zeitraumGesperrt} onClick={onExport}>
          {exportLaeuft ? "CSV …" : "CSV"}
        </button>
      </div>
    </div>
  );
}
